import { FormRootConfig } from "types/form";

export const BASE_URL = window.FZ_API_BASE_URL;

/**
 * Тип-алиас для значений BigDecimal из бэкенда.
 * Рекомендуется хранить как string для сохранения точности.
 */
export type BigDecimal = string;

/** =========================
 *       ENUM / LITERALы
 *  ========================= */
export type AddressOwnType = 'property' | 'rent';
export type Gender = 'male' | 'female';
export type SoleExecutiveBodyType = 'companyHead' | 'managementCompany';

/** =========================
 *          DTOs
 *  ========================= */

export interface CustomerModel {
  components: CustomerComponents;
}

export interface CustomerComponents {
  customerAddresses?: CustomerAddressesComponentModel;
  customerBankAccounts?: CustomerBankAccountsComponentModel;
  customerCard?: CustomerCardComponentModel;
  customerFinReport?: CustomerFinReportComponentModel;
  customerFounderOrgs?: CustomerFounderOrgsComponentModel;
  customerManagement?: CustomerManagementComponentModel;
  customerPersons?: CustomerPersonsComponentModel;
  customerStaff?: CustomerStaffComponentModel;
  principalContacts?: PrincipalContacts;
}

/* ---- Addresses ---- */
export interface CustomerAddressesComponentModel {
  addresses?: CustomerAddresses;
}

export interface CustomerAddresses {
  legal?: CustomerAddress;
  actual?: CustomerAddress;
  postal?: CustomerAddress;
}

export interface CustomerAddress {
  address?: string;
  ownType?: AddressOwnType;
}

/* ---- Bank accounts ---- */
export interface CustomerBankAccountsComponentModel {
  bankAccounts?: BankAccount[];
}

export interface BankAccount {
  id?: string;
  bank?: Bank;
  accountNumber?: string;
  cardFile2?: boolean;
  cardFile2Amount?: BigDecimal;
}

export interface Bank {
  name?: string;
  bic?: string;
  corrNumber?: string;
}

/* ---- Card ---- */
export interface CustomerCardComponentModel {
  customerId?: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  displayName?: string;
  shortName?: string;
  fullName?: string;
}

/* ---- Fin report ---- */
export interface CustomerFinReportComponentModel {
  taxationType?: string;
  latestPeriod?: string;
  periods?: string[];
  reports?: Record<string, Record<string, BigDecimal>>;
}

/* ---- Founder orgs ---- */
export interface CustomerFounderOrgsComponentModel {
  founderOrgs?: FounderOrg[];
}

export interface FounderOrg {
  companyId?: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  name?: string;
  sharePercent?: BigDecimal;
  shareAmount?: BigDecimal;
}

/* ---- Management ---- */
export interface CustomerManagementComponentModel {
  management?: CustomerManagement;
}

export interface CustomerManagement {
  supremeGoverningBody?: string;
  soleExecutiveBody?: SoleExecutiveBodyType;
  managementCompany?: ManagementCompany;
  supervisoryBoard?: ManagementBody;
  collegialExecutiveBody?: ManagementBody;
}

export interface ManagementCompany {
  inn?: string;
  kpp?: string;
  ogrn?: string;
  name?: string;
}

export interface ManagementBody {
  /** Поле называется "exists" в JSON */
  exists: boolean;
  name?: string;
}

/* ---- Persons ---- */
export interface CustomerPersonsComponentModel {
  persons?: CustomerPerson[];
}

export interface CustomerPerson {
  person?: Person;
  personRole?: PersonRole;
}

export interface Person {
  id?: string;
  ownerId?: string;
  inn?: string;
  snils?: string;
  displayName?: string;
  lastName?: string;
  firstName?: string;
  secondName?: string;
  /** ISO дата YYYY-MM-DD */
  birthDate?: string;
  birthPlace?: string;
  registrationAddress?: string;
  actualAddress?: string;
  phone?: string;
  email?: string;
  gender?: Gender;
  identityDocument?: IdentityDocument;
  citizenship?: string;
  countryOfResidence?: string;
  ipdl?: PersonPdl;
  mpdl?: PersonPdl;
  rpdl?: PersonPdl;
}

export interface IdentityDocument {
  type?: string;
  series?: string;
  number?: string;
  issuedBy?: string;
  issuedByCode?: string;
  /** ISO дата YYYY-MM-DD */
  issuedDate?: string;
  /** ISO дата YYYY-MM-DD */
  expiryDate?: string;
}

export interface PersonPdl {
  /** Поле называется "isPdl" в JSON */
  isPdl: boolean;
  pdlPosition?: string;
  pdlOrganization?: string;
  pdlOrganizationAddress?: string;
  pdlRelation?: string;
}

/* ---- PersonRole ---- */
export interface PersonRole {
  /** Руководитель компании */
  isHead?: boolean;
  /** Подписант */
  isSigner?: boolean;
  /** Учредитель */
  isFounder?: boolean;
  /** Бенефициарный владелец */
  isBeneficiaryOwner?: boolean;
  /** Член наблюдательного совета */
  isSupervisoryBoardMember?: boolean;
  /** Член коллегиального исполнительного органа */
  isCollegialExecutiveBodyMember?: boolean;

  /** Информация о сотруднике */
  employee?: PersonRoleEmployee;
  /** Информация о учредителе */
  founder?: PersonRoleFounder;
  /** Информация о бенефициарном владельце */
  beneficiaryOwner?: PersonRoleBeneficiaryOwner;
}

export interface PersonRoleEmployee {
  /** Должность */
  position?: string;
  /** Дата назначения (YYYY-MM-DD) */
  positionDateStart?: string;
  /** Дата истечения полномочий (YYYY-MM-DD) */
  positionDateEnd?: string;
  /** Документ о назначении */
  authorizingDoc?: PersonRoleAuthorizingDoc;
  /** Представитель действует по доверенности */
  byProxy?: boolean;
  /** Информация о доверенности */
  proxy?: PersonRoleProxy;
}

export interface PersonRoleAuthorizingDoc {
  /** Наименование документа (Устав/Приказ/Протокол/Решение) */
  authorizingDocName?: string;
  /** Номер документа */
  authorizingDocNumber?: string;
  /** Дата документа (YYYY-MM-DD) */
  authorizingDocDate?: string;
}

export interface PersonRoleProxy {
  proxyName?: string;
  proxyNumber?: string;
  /** Дата доверенности (YYYY-MM-DD) */
  proxyDateStart?: string;
  /** Срок действия доверенности (YYYY-MM-DD) */
  proxyDateEnd?: string;
  /** Ограничение по сумме гарантии */
  proxyMaxGuaranteeAmount?: BigDecimal;
  /** Ограничение по сумме договора */
  proxyMaxContractAmount?: BigDecimal;
}

export interface PersonRoleFounder {
  /** Доля, % */
  sharePercent?: BigDecimal;
  /** Доля, руб */
  shareAmount?: BigDecimal;
}

export interface PersonRoleBeneficiaryOwner {
  /** Доля, % */
  sharePercent?: BigDecimal;
  /** Доля, руб */
  shareAmount?: BigDecimal;
  /** Основание наличия статуса */
  reason?: string;
}

/* ---- Staff ---- */
export interface CustomerStaffComponentModel {
  staff?: CustomerStaff;
}

export interface CustomerStaff {
  staffCount?: number;              // Long -> number
  staffWageFund?: BigDecimal;       // BigDecimal -> string
  staffWageDebt?: BigDecimal;       // BigDecimal -> string
}

export interface PrincipalContacts {
  attributes: {
    principalContactPhone?: string;
    principalContactLastName?: string;
    principalContactFirstName?: string;
    principalContactSecondName?: string;
    principalContactEmail?: string;
  };
};

/** Получить данные по анкете */

export async function fetchCustomerModel(
  customerId: string,
  accessToken: string
): Promise<CustomerModel> {
  const url = `${BASE_URL}/front-bg/card/customer/${encodeURIComponent(customerId)}/model`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data: CustomerModel = await r.json();
  return data;
}
export async function fetchCustomerConfig(
  customerId: string,
  accessToken: string
): Promise<FormRootConfig> {
  const url = `${BASE_URL}/front-bg/card/customer/${encodeURIComponent(customerId)}/config`;

  const r = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data: FormRootConfig = await r.json();
  return data;
}

export async function saveCustomerModel(
  customerId: string,
  model: CustomerModel,
  accessToken: string
): Promise<CustomerModel> {
  const url = `${BASE_URL}/front-bg/card/customer/${encodeURIComponent(customerId)}/model`;

  const r = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(model),
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data: CustomerModel = await r.json();
  return data;
}

/** Создать анкету */
export interface CustomerRequest {
  inn: string;
}

export interface CustomerResponse {
  customerId: string;
}

export async function getCustomerByInn(
  inn: string,
  accessToken: string
): Promise<CustomerResponse> {
  const url = `${BASE_URL}/front-bg/customer`;

  const r = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ inn }),
  });

  if (!r.ok) throw new Error('HTTP ' + r.status);

  const data: CustomerResponse = await r.json();
  return data;
}
